<template>
  <div>
    <div class="font-weight-medium">{{ label }}</div>

    <div v-if="value" class="d-flex align-center mt-1 pa-1 pr-3 grey lighten-4" style="border-radius: 28px;">
      <audio
        ref="audio"
        :src="value"
        preload="metadata"
        @loadedmetadata="alCargar"
        @durationchange="alCambiarDuracion"
        @timeupdate="alAvanzar"
        @play="alReproducir"
        @pause="reproduciendo = false"
        @ended="reproduciendo = false"
      ></audio>

      <v-btn fab small depressed color="primary" :loading="calculando" @click="alternar">
        <v-icon>{{ reproduciendo ? 'mdi-pause' : 'mdi-play' }}</v-icon>
      </v-btn>

      <v-slider
        :value="actual"
        :max="duracion || 1"
        step="0"
        hide-details
        dense
        class="mx-3"
        :disabled="calculando || !duracion"
        @start="arrastrando = true"
        @end="arrastrando = false"
        @change="mover"
      ></v-slider>

      <span class="text-caption grey--text text--darken-2" style="min-width: 78px; text-align: right;">
        {{ formato(actual) }} / {{ formato(duracion) }}
      </span>

      <v-btn x-small text class="ml-1 px-1" style="min-width: 36px;" @click="cambiarVelocidad">
        {{ velocidad }}x
      </v-btn>
    </div>
    <div v-else-if="!readonly" class="grey--text text--darken-1">Sin audio.</div>

    <v-expansion-panels v-if="value && texto" flat class="mt-2">
      <v-expansion-panel>
        <v-expansion-panel-header class="px-0 subtitle-2">
          <v-icon left small color="deep-purple">mdi-microphone-message</v-icon>
          Transcripción
        </v-expansion-panel-header>
        <v-expansion-panel-content class="body-2" style="white-space: pre-wrap;">
          {{ texto }}
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <v-btn v-else-if="value && transcribible && registroId" small tile outlined color="primary" class="mt-2" :loading="transcribiendo" @click="transcribir">
      <v-icon left small>mdi-text-box-outline</v-icon>
      Transcribir
    </v-btn>

    <div v-if="!readonly" class="mt-2">
      <v-btn v-if="!grabando" color="primary" small tile class="mr-2" :loading="subiendo || iniciando" :disabled="subiendo || iniciando || disabled" @click="iniciar">
        <v-icon left small>mdi-microphone</v-icon>
        {{ value ? 'Grabar de nuevo' : 'Grabar audio' }}
      </v-btn>
      <v-btn v-else color="red" dark small tile class="mr-2" @click="detener">
        <v-icon left small>mdi-stop</v-icon>
        Detener ({{ segundos }}s)
      </v-btn>

      <v-btn v-if="!grabando" color="primary" small tile outlined class="mr-2" :disabled="subiendo || disabled" @click="$refs.archivo.click()">
        <v-icon left small>mdi-upload</v-icon>
        Subir archivo
      </v-btn>
      <input ref="archivo" type="file" accept="audio/*" style="display:none" @change="elegirArchivo">

      <v-btn v-if="value && !grabando" color="red" small tile outlined :disabled="subiendo || disabled" @click="$emit('input', null)">
        <v-icon left small>mdi-delete</v-icon>
        Quitar
      </v-btn>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

// Formatos en orden de preferencia; Safari/iPhone solo graba mp4
const FORMATOS = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']

export default {
  name: 'AudioRetro',

  props: {
    value   : { type: String, default: null },
    label   : { type: String, default: 'Audio de retroalimentación' },
    readonly: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    campo        : { type: String, default: null },
    registroId   : { type: Number, default: null },
    transcripcion: { type: String, default: null },
    transcribible: { type: Boolean, default: false }
  },

  data: () => ({
    texto: null,
    transcribiendo: false,
    grabando: false,
    iniciando: false,
    subiendo: false,
    segundos: 0,
    recorder: null,
    timer   : null,
    reproduciendo: false,
    calculando : false,
    arrastrando: false,
    actual  : 0,
    duracion: 0,
    velocidad: 1
  }),

  created () {
    this.texto = this.transcripcion
  },

  mounted () {
    this.$root.$on('audio-retro:play', this.alReproducirOtro)
  },

  watch: {
    transcripcion (v) {
      this.texto = v
    },

    value () {
      this.pausar()
      this.reproduciendo = false
      this.calculando = false
      this.arrastrando = false
      this.actual = 0
      this.duracion = 0
      this.velocidad = 1
    }
  },

  beforeDestroy () {
    this.$root.$off('audio-retro:play', this.alReproducirOtro)
    this.pausar()
    this.liberar()
    this.cancelar()
  },

  methods: {
    alCargar () {
      const a = this.$refs.audio
      a.playbackRate = this.velocidad
      if (isFinite(a.duration)) {
        this.duracion = a.duration
        return
      }
      // Las grabaciones webm del navegador no traen duración: se salta al final
      // para que el navegador la calcule y se regresa al inicio en durationchange
      this.calculando = true
      a.currentTime = 1e101
      // Si el navegador nunca reporta la duración, se libera el reproductor igual
      setTimeout(() => {
        if (!this.calculando || this.$refs.audio !== a) return
        a.currentTime = 0
        this.actual = 0
        this.calculando = false
      }, 4000)
    },

    alCambiarDuracion () {
      const a = this.$refs.audio
      if (!a || !isFinite(a.duration)) return
      this.duracion = a.duration
      if (this.calculando) {
        a.currentTime = 0
        this.actual = 0
        this.calculando = false
      }
    },

    alAvanzar () {
      // Mientras se calcula la duración o se arrastra la barra, el tiempo del audio no manda
      if (this.calculando || this.arrastrando) return
      this.actual = this.$refs.audio.currentTime
    },

    alReproducir () {
      this.reproduciendo = true
      // Solo un audio sonando a la vez en la página
      this.$root.$emit('audio-retro:play', this)
    },

    alReproducirOtro (origen) {
      if (origen !== this) this.pausar()
    },

    pausar () {
      const a = this.$refs.audio
      if (a && !a.paused) a.pause()
    },

    async transcribir () {
      this.transcribiendo = true
      try {
        const { data } = await this.$http.post('retroalimentacion.transcribir', { campo: this.campo, id: this.registroId })
        this.texto = data.texto
      } catch (e) {
        this.error(e.response && e.response.data && e.response.data.message ? e.response.data.message : 'No se pudo transcribir el audio')
      } finally {
        this.transcribiendo = false
      }
    },

    alternar () {
      const a = this.$refs.audio
      if (!a || this.calculando) return
      if (!a.paused) return a.pause()
      const p = a.play()
      if (p && p.catch) p.catch(() => { this.reproduciendo = false })
    },

    // Solo se busca al soltar o dar clic en la barra (@change); con @input el
    // slider re-emitía en cada timeupdate y el audio saltaba hacia atrás sin parar
    mover (v) {
      this.arrastrando = false
      const a = this.$refs.audio
      if (!a || this.calculando || !isFinite(v)) return
      a.currentTime = v
      this.actual = v
    },

    cambiarVelocidad () {
      this.velocidad = this.velocidad === 1 ? 1.5 : this.velocidad === 1.5 ? 2 : 1
      if (this.$refs.audio) this.$refs.audio.playbackRate = this.velocidad
    },

    formato (s) {
      const t = Math.floor(s || 0)
      return Math.floor(t / 60) + ':' + String(t % 60).padStart(2, '0')
    },

    async iniciar () {
      if (this.iniciando || this.grabando) return
      if (!navigator.mediaDevices || !window.MediaRecorder) {
        return this.error('Este navegador no permite grabar. Usa "Subir archivo".')
      }
      this.pausar()
      this.iniciando = true
      let stream
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
        })
      } catch (e) {
        this.iniciando = false
        return this.error('No se pudo acceder al micrófono. Revisa el permiso del navegador.')
      }
      try {
        const tipo = MediaRecorder.isTypeSupported ? FORMATOS.find(f => MediaRecorder.isTypeSupported(f)) : null
        const recorder = new MediaRecorder(stream, tipo ? { mimeType: tipo } : undefined)
        const chunks = []
        recorder.ondataavailable = e => { if (e.data && e.data.size) chunks.push(e.data) }
        recorder.onerror = () => {
          recorder.onstop = null
          stream.getTracks().forEach(t => t.stop())
          this.liberar()
          this.error('Se interrumpió la grabación. Intenta de nuevo.')
        }
        recorder.onstop = () => {
          stream.getTracks().forEach(t => t.stop())
          const mime = (recorder.mimeType || tipo || 'audio/webm').split(';')[0]
          const blob = new Blob(chunks, { type: mime })
          if (!blob.size) return this.error('La grabación quedó vacía. Intenta de nuevo.')
          const ext = mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm'
          this.subir(new File([blob], 'grabacion.' + ext, { type: mime }))
        }
        // Un trozo por segundo para no perder lo grabado si algo falla a medias
        recorder.start(1000)
        this.recorder = recorder
        this.segundos = 0
        this.grabando = true
        this.timer = setInterval(() => this.segundos++, 1000)
      } catch (e) {
        stream.getTracks().forEach(t => t.stop())
        this.error('No se pudo iniciar la grabación en este navegador. Usa "Subir archivo".')
      } finally {
        this.iniciando = false
      }
    },

    detener () {
      this.liberar()
      if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop()
    },

    liberar () {
      clearInterval(this.timer)
      this.grabando = false
    },

    cancelar () {
      if (this.recorder && this.recorder.state !== 'inactive') {
        this.recorder.onstop = () => this.recorder.stream.getTracks().forEach(t => t.stop())
        this.recorder.stop()
      }
    },

    elegirArchivo (e) {
      const archivo = e.target.files[0]
      e.target.value = ''
      if (archivo) this.subir(archivo)
    },

    async subir (archivo) {
      this.subiendo = true
      try {
        const formData = new FormData()
        formData.append('file', archivo)
        const { data } = await this.$http.post('retroalimentacion.upload.audio', formData)
        if (!data || !data.audio_url) throw new Error('No se recibió la URL del audio')
        this.$emit('input', axios.defaults.baseURL + 'imagenes-retro/' + data.audio_url)
      } catch (e) {
        this.error(e.response && e.response.data && e.response.data.message ? e.response.data.message : 'No se pudo subir el audio')
      } finally {
        this.subiendo = false
      }
    },

    error (mensaje) {
      this.$emit('error', mensaje)
    }
  }
}
</script>
